import {DEFAULT_LOCALE_ISO, Messages, toJavaLocaleFormat} from "./Messages";
import {MessagesTokenizer, TokenizedResourceString} from "./MessagesTokenizer";
import {hasValue, notSet} from "../utils/ValueUtils";

type ResourceEntry = string | TokenizedResourceString;
type ResourceStringMap = Map<string, ResourceEntry>;

function keyValue(
    map: Map<string, ResourceStringMap>,
    locale: string,
    key: string
): ResourceEntry | undefined {
    const val = locale && map?.get(locale)?.get(key);
    return hasValue(val) ? val : undefined;
}

export function objectToMap(obj: object): ResourceStringMap {
    const map = new Map<string, ResourceEntry>();
    for (const [key, value] of Object.entries(obj)) {
        if (hasValue(value)) {
            map.set(key, String(value));
        }
    }
    return map;
}

// WARNING: There is a duplicated version of this in the Portal code
export abstract class MessagesBase implements Messages {
    readonly locale: string;
    readonly map = new Map<string, ResourceStringMap>();

    protected constructor(locale?: string) {
        this.locale = toJavaLocaleFormat(locale) || DEFAULT_LOCALE_ISO;
    }

    protected setMessageData(locale: string, data: object) {
        if (locale && data) {
            this.map.set(locale, objectToMap(data));
        }
    }

    has(key: string | null | undefined): boolean {
        if (!key) {
            return false;
        }
        return hasValue(keyValue(this.map, this.locale, key));
    }

    private processEscapeCharacters(v: string): string {
        // Handle escape characters. Done separately from the tokenizing loop below
        // because escape characters are active in quoted strings.
        let value = v;
        let i = 0;
        while ((i = value.indexOf('\\', i)) !== -1) {
            if (value.charAt(i + 1) === 't') {
                value = value.substring(0, i) + '\t' + value.substring((i++) + 2); // tab
            } else if (value.charAt(i + 1) === 'r') {
                value = value.substring(0, i) + '\r' + value.substring((i++) + 2); // return
            } else if (value.charAt(i + 1) === 'n') {
                value = value.substring(0, i) + '\n' + value.substring((i++) + 2); // line feed
            } else if (value.charAt(i + 1) === 'f') {
                value = value.substring(0, i) + '\f' + value.substring((i++) + 2); // form feed
            } else if (value.charAt(i + 1) === '\\') {
                value = value.substring(0, i) + '\\' + value.substring((i++) + 2); // \
            } else {
                value = value.substring(0, i) + value.substring(i + 1); // Quietly drop the character
            }
        }
        return value;
    }

    private processValue(entry: ResourceEntry, key: string): TokenizedResourceString {
        // If the resource string has already been converted to a
        // ProcessedResourceString, just return it as it
        if (typeof entry !== 'string') {
            return entry;
        }

        const resourceString = this.processEscapeCharacters(entry);

        // Lazily convert the string to a list of tokens.
        const processedEntry = MessagesTokenizer.process(resourceString);

        // Cache the tokenized results for next time (Make the array the value for the entry)
        let m = this.map.get(this.locale);
        if (!m) {
            m = new Map<string, ResourceEntry>()
            this.map.set(this.locale, m);
        }
        m.set(key, processedEntry);

        return processedEntry;
    }

    private buildResultString(processedEntry: TokenizedResourceString, args: unknown[]): string {
        let str = "";
        for (const textOrIndex of processedEntry) {
            if (typeof textOrIndex === "number") {
                // Indexed parameter
                if (args && textOrIndex < args.length && hasValue(args[textOrIndex])) {
                    str += String(args[textOrIndex]) || ""; // NOSONAR(typescript:S6551)
                } else {
                    // We don't have enough arguments, so output a placeholder into the string
                    str += "{" + textOrIndex + "}";
                }
            } else {
                // A string, or something we are just going to coerce to a string anyway
                str += textOrIndex;
            }
        }
        return str;
    }

    prop(key: string | null | undefined, ...args: unknown[]): string {
        if (!key) {
            return "";
        }
        let argArray;
        if (args.length === 1 && Array.isArray(args[0])) {
            argArray = args[0];
        } else {
            argArray = args;
        }

        const resourceEntry = keyValue(this.map, this.locale, key);
        if (notSet(resourceEntry)) {
            return '[' + key + ']';
        }
        const processedEntry = this.processValue(resourceEntry, key);

        if (processedEntry.length === 0) {
            return "";
        }
        if (processedEntry.length === 1 && typeof processedEntry[0] === "string") {
            return processedEntry[0];
        }

        return this.buildResultString(processedEntry, argArray)
    }
}
